import type { CellComponent, ColumnComponent, RowComponent, TabulatorFull as Tabulator } from 'tabulator-tables';
import { tx } from '../../i18n';
import { recordUndo, redo, undo } from './undo';

/**
 * Копирование и вставка в таблицах — как в Excel.
 *
 * Tabulator умеет это сам, но ненадёжно: вставку он разбирает как таблицу с заголовками,
 * а копирование срабатывает, только если фокус браузера точно в таблице.
 * Поэтому буфер обмена обрабатывается здесь, на уровне всей страницы:
 * - активна та таблица, по которой щёлкнули последней; щелчок мимо таблицы снимает выделение;
 * - Ctrl+C копирует выделенные ячейки (строки, столбцы) в виде, который понимает Excel;
 * - Ctrl+X — то же, плюс очистка ячеек (в таблицах ввода);
 * - Delete / Backspace — очищает все выделенные ячейки: диапазон, целый столбец или строку (в таблицах ввода);
 * - Ctrl+V вставляет блок из Excel, начиная с выделенной ячейки: вниз по строкам и вправо по столбцам.
 *   Не хватает строк — они добавляются. Одно значение на выделенный диапазон — заполняет весь диапазон.
 *   Столбцы, которые заполняет программа (серые), при вставке пропускаются.
 */
export interface GridHandle {
  table: Tabulator;
  element: HTMLElement;
  /** Таблица ввода: можно вставлять, вырезать, добавлять строки. */
  editable: boolean;
  /** Пустая строка для добавления при вставке длинного блока. */
  blankRow?: () => Record<string, string>;
  /** Данные изменены вставкой или вырезанием. */
  onChanged?: () => void;
}

const grids = new Set<GridHandle>();
let active: GridHandle | null = null;

export function registerGrid(handle: GridHandle): () => void {
  grids.add(handle);
  ensureListeners();
  return () => {
    grids.delete(handle);
    if (active === handle) active = null;
  };
}

/**
 * Снять выделение (щелчок мимо таблицы). Сам диапазон Tabulator не удаляется — без него ломаются его обработчики,
 * — таблица просто перестаёт его показывать и отвечать на Ctrl+C / Ctrl+V до следующего щелчка по ней.
 */
export function clearSelection(handle: GridHandle): void {
  handle.element.classList.add('parts-table--idle');
  const focused = document.activeElement as HTMLElement | null;
  if (focused && handle.element.contains(focused)) focused.blur();
}

function activate(handle: GridHandle): void {
  handle.element.classList.remove('parts-table--idle');
}

let listening = false;

function ensureListeners(): void {
  if (listening) return;
  listening = true;

  document.addEventListener(
    'mousedown',
    (e) => {
      const target = e.target as HTMLElement;
      const hit = [...grids].find((g) => g.element.contains(target)) ?? null;
      if (hit) {
        if (active && active !== hit) clearSelection(active);
        activate(hit);
        active = hit;
        return;
      }
      // Меню, подсказки и окна поверх страницы не снимают выделение
      if (target.closest('.mantine-Popover-dropdown, .mantine-Menu-dropdown, .mantine-Combobox-dropdown, .mantine-Modal-root')) return;
      if (active) {
        clearSelection(active);
        active = null;
      }
    },
    true
  );

  // Ctrl+Z — отменить, Ctrl+Y или Ctrl+Shift+Z — повторить (в таблицах ввода)
  document.addEventListener('keydown', (e) => {
    if (!active || !active.editable || fromTextField(e)) return;
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
    const key = e.key.toLowerCase();
    const isUndo = (key === 'z' || e.code === 'KeyZ') && !e.shiftKey;
    const isRedo = key === 'y' || e.code === 'KeyY' || ((key === 'z' || e.code === 'KeyZ') && e.shiftKey);
    if (!isUndo && !isRedo) return;
    e.preventDefault();
    const grid = active;
    void (isUndo ? undo(grid.table) : redo(grid.table)).then((changed) => {
      if (changed) grid.onChanged?.();
    });
  });

  // Delete / Backspace: очистить всё выделенное. Перехватываем раньше Tabulator (фаза захвата):
  // его собственная очистка спотыкается о столбец номеров строк и стирает только одну ячейку.
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (!active || !active.editable || fromTextField(e) || e.ctrlKey || e.metaKey || e.altKey) return;
      const grid = active;
      const cells = selectedCells(grid.table).flat().filter((c) => isEditable(c.getColumn()));
      if (!cells.length) return;
      e.preventDefault();
      e.stopPropagation();
      void clearCells(grid, cells);
    },
    true
  );

  document.addEventListener('copy', (e) => onCopy(e, false));
  document.addEventListener('cut', (e) => onCopy(e, true));
  document.addEventListener('paste', onPaste);
}

/** Событие пришло из обычного поля ввода (поиск, форма, редактор ячейки) — не мешаем браузеру. */
function fromTextField(e: Event): boolean {
  const target = e.target as HTMLElement | null;
  if (!target) return false;
  if (target.closest('.tabulator-editing')) return true;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
}

function asCell(cell: unknown): CellComponent | null {
  if (!cell) return null;
  const c = cell as { getColumn?: unknown; getComponent?: () => CellComponent };
  if (typeof c.getColumn === 'function') return cell as CellComponent;
  return typeof c.getComponent === 'function' ? c.getComponent() : null;
}

function lastRange(table: Tabulator) {
  const ranges = table.getRanges();
  return ranges[ranges.length - 1] ?? null;
}

function isEditable(col: ColumnComponent): boolean {
  return !!col.getDefinition().editor;
}

/** Значение для буфера: числа без неразрывных пробелов, чтобы Excel понял их как числа. */
function clipValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let text = tx(String(value));
  if (/^-?[\d\u00A0\s]+([.,]\d+)?$/.test(text)) text = text.replace(/[\u00A0\s]/g, '');
  return /[\t\n"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function selectedCells(table: Tabulator): CellComponent[][] {
  const range = lastRange(table);
  if (!range) return [];
  const rows = range.getStructuredCells() as unknown as unknown[][];
  return rows.map((row) => row.map(asCell).filter((c): c is CellComponent => !!c && !!c.getField() && !c.getField().startsWith('__')));
}

function onCopy(e: ClipboardEvent, cut: boolean): void {
  if (!active || fromTextField(e)) return;
  const cells = selectedCells(active.table).filter((row) => row.length);
  if (!cells.length) return;
  const text = cells.map((row) => row.map((c) => clipValue(c.getValue())).join('\t')).join('\r\n');
  e.clipboardData?.setData('text/plain', text);
  e.preventDefault();
  if (cut && active.editable) void clearCells(active, cells.flat().filter((c) => isEditable(c.getColumn())));
}

/** Очистить ячейки одной операцией: по строке за раз, один шаг отмены (Ctrl+Z). */
async function clearCells(grid: GridHandle, cells: CellComponent[]): Promise<void> {
  if (!cells.length) return;
  recordUndo(grid.table);
  const byRow = new Map<RowComponent, Record<string, string>>();
  for (const c of cells) {
    const row = c.getRow();
    const patch = byRow.get(row) ?? {};
    patch[c.getField()] = '';
    byRow.set(row, patch);
  }
  await Promise.all([...byRow].map(([row, patch]) => row.update(patch)));
  grid.onChanged?.();
}

/** Разбор текста из Excel: столбцы через Tab, строки через перевод строки, значения в кавычках могут содержать переводы строк. */
export function parseClipboard(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
      continue;
    }
    if (ch === '"' && cell === '') quoted = true;
    else if (ch === '\t') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  // Excel добавляет пустую строку в конце
  while (rows.length && rows[rows.length - 1]!.every((v) => v === '')) rows.pop();
  return rows;
}

function onPaste(e: ClipboardEvent): void {
  const grid = active;
  if (!grid || !grid.editable || fromTextField(e)) return;
  const text = e.clipboardData?.getData('text/plain') ?? '';
  const matrix = parseClipboard(text);
  if (!matrix.length) return;
  e.preventDefault();
  void pasteMatrix(grid, matrix);
}

async function pasteMatrix(grid: GridHandle, matrix: string[][]): Promise<void> {
  const { table } = grid;
  const range = lastRange(table);
  if (!range) return;
  const bounds = range.getBounds();
  const start = asCell(bounds.start);
  const end = asCell(bounds.end);
  if (!start || !end) return;

  const columns = table.getColumns().filter((c) => c.isVisible() && !!c.getField());
  const startColPos = Math.max(0, columns.findIndex((c) => c.getField() === start.getColumn().getField()));
  const endColPos = Math.max(startColPos, columns.findIndex((c) => c.getField() === end.getColumn().getField()));
  let rows = table.getRows('active');
  const startRowPos = Math.max(0, rows.findIndex((r) => r.getElement() === start.getRow().getElement()));
  const endRowPos = Math.max(startRowPos, rows.findIndex((r) => r.getElement() === end.getRow().getElement()));

  // Одно значение на выделенный диапазон — заполнить весь диапазон
  const single = matrix.length === 1 && matrix[0]!.length === 1;
  const height = single ? endRowPos - startRowPos + 1 : matrix.length;
  const targets = columns.slice(startColPos).filter(isEditable);
  const width = single ? columns.slice(startColPos, endColPos + 1).filter(isEditable).length : Math.max(...matrix.map((r) => r.length));

  recordUndo(table);
  const need = startRowPos + height - rows.length;
  if (need > 0 && grid.blankRow) {
    await table.addData(Array.from({ length: need }, () => grid.blankRow!()), false);
    rows = table.getRows('active');
  }

  const updates: Promise<void>[] = [];
  let lastRow: RowComponent | null = null;
  let lastCol: ColumnComponent | null = null;
  for (let i = 0; i < height; i++) {
    const row = rows[startRowPos + i];
    if (!row) break;
    const patch: Record<string, string> = {};
    for (let j = 0; j < width; j++) {
      const col = targets[j];
      if (!col) break;
      patch[col.getField()] = (single ? matrix[0]![0]! : (matrix[i]![j] ?? '')).trim();
      lastCol = col;
    }
    if (Object.keys(patch).length) {
      updates.push(row.update(patch));
      lastRow = row;
    }
  }
  await Promise.all(updates);

  // Выделить вставленный блок
  if (lastRow && lastCol && targets[0]) {
    try {
      const first = rows[startRowPos]!.getCell(targets[0]);
      const last = lastRow.getCell(lastCol);
      const mod = (table as unknown as { modules?: { selectRange?: { clearRanges?: () => void } } }).modules?.selectRange;
      mod?.clearRanges?.();
      (table as unknown as { addRange: (a: CellComponent, b: CellComponent) => void }).addRange(first, last);
    } catch {
      /* выделение не обязательно */
    }
  }
  grid.onChanged?.();
}
